import * as React from 'react';
import { CollectionGridContext } from './collection-grid-context';

interface CollectionGridProviderProps extends React.PropsWithChildren {
  /**
   * The ID of the portfolio to fetch collections for
   *
   * @optional
   * @type {string}
   */
  portfolioId?: string;
  /**
   * The ID of the organization
   *
   * @optional
   * @type {string}
   */
  organizationId?: string;
  /**
   * Number of items to fetch per page
   *
   * @optional
   * @type {number}
   * @default 12
   */
  limit?: number;
}

/**
 * Provider component for the collection grid context.
 * Manages the state for collection grid pagination.
 *
 * @param props - The provider props
 */
function CollectionGridProvider(props: CollectionGridProviderProps) {
  const { children, portfolioId, organizationId, limit = 12 } = props;

  const value = React.useMemo(
    () => ({
      portfolioId,
      organizationId,
      limit,
    }),
    [portfolioId, organizationId, limit],
  );

  return <CollectionGridContext.Provider value={value}>{children}</CollectionGridContext.Provider>;
}

/**
 * Hook to access the collection grid context.
 * Must be used within a CollectionGridProvider.
 *
 * @throws Error if used outside of CollectionGridProvider
 * @returns The collection grid context
 */
const useCollectionGrid = () => {
  const context = React.useContext(CollectionGridContext);

  if (!context) {
    throw new Error('useCollectionGrid must be used within a CollectionGridProvider');
  }

  return context;
};

export { CollectionGridProvider, useCollectionGrid };
